/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from "react";
import { Card, message, Space, Row, Col, Flex, Segmented } from "antd";
import { Form, Select, DatePicker, Button, Table, Typography } from "antd";
import { CheckOutlined, FileExcelOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import SOService from "../../service/SO.service";
import OptionService from "../../service/Options.service";
import { comma } from "../../utils/util";
import "./report.css";

const soservice = SOService();
const opService = OptionService();
const RangePicker = DatePicker.RangePicker;

const OrderItemsByBranch = () => {
  const [form] = Form.useForm();
  const [rawData, setRawData] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  // "branch" = สรุปยอดรวมต่อสาขา (ตารางแคบ อ่านง่าย), "daily" = แยกรายวัน
  const [displayMode, setDisplayMode] = useState("branch");

  const getReport = () => {
    const v = form.getFieldsValue();
    const [from, to] = v.daterange || [];
    const parm = {
      sodate_form: from ? dayjs(from).format("YYYY-MM-DD") : null,
      sodate_to: to ? dayjs(to).format("YYYY-MM-DD") : null,
      cuscode: v.cuscode || [],
      doc_status: v.doc_status || [],
    };
    soservice
      .reportByBranch(parm)
      .then((res) => {
        const { data, statuses } = res.data;
        setRawData(data || []);
        setStatusOptions(
          (statuses || []).map((s) => ({ value: s, label: s }))
        );
      })
      .catch((err) => {
        console.log(err);
        message.error("Request error!");
      });
  };

  useEffect(() => {
    form.setFieldValue("daterange", [dayjs().startOf("month"), dayjs()]);
    opService.optionsCustomer().then((res) => {
      const { data } = res.data;
      setCustomerOptions(
        (data || []).map((c) => ({
          value: c.cuscode,
          label: `${c.cuscode} ${c.cusname || ""}`,
        }))
      );
    });
    getReport();
  }, []);

  /* ===== Pivot: rows = สินค้า, columns = สาขา(ลูกค้า) x วันที่ ===== */
  const { branches, rows } = useMemo(() => {
    const branchMap = new Map(); // cuscode -> { cuscode, cusname, dates:Set }
    const rowMap = new Map(); // stcode -> row
    rawData.forEach((r) => {
      if (!branchMap.has(r.cuscode)) {
        branchMap.set(r.cuscode, {
          cuscode: r.cuscode,
          cusname: r.cusname || r.cuscode,
          dates: new Set(),
        });
      }
      branchMap.get(r.cuscode).dates.add(r.sodate);

      if (!rowMap.has(r.stcode)) {
        rowMap.set(r.stcode, {
          stcode: r.stcode,
          stname: r.stname,
          total: 0,
        });
      }
      const row = rowMap.get(r.stcode);
      const key = `${r.cuscode}|${r.sodate}`;
      const qty = Number(r.qty || 0);
      row[key] = (row[key] || 0) + qty;
      row[`sum|${r.cuscode}`] = (row[`sum|${r.cuscode}`] || 0) + qty;
      row.total += qty;
    });
    const branches = [...branchMap.values()]
      .sort((a, b) => a.cusname.localeCompare(b.cusname))
      .map((b) => ({ ...b, dates: [...b.dates].sort() }));
    const rows = [...rowMap.values()];
    return { branches, rows };
  }, [rawData]);

  const columns = useMemo(() => {
    const cols = [
      {
        title: "สินค้า",
        key: "stname",
        fixed: "left",
        width: 220,
        render: (_, r) => (
          <Typography.Text strong>
            {r.stcode} {r.stname}
          </Typography.Text>
        ),
        sorter: (a, b) => (a.stcode || "").localeCompare(b.stcode || ""),
      },
      ...branches.map((b) =>
        displayMode === "daily"
          ? {
              title: b.cusname,
              key: b.cuscode,
              align: "center",
              children: b.dates.map((d) => ({
                title: dayjs(d).format("DD/MM/YYYY"),
                key: `${b.cuscode}|${d}`,
                dataIndex: `${b.cuscode}|${d}`,
                align: "right",
                width: 110,
                render: (v) => (v ? comma(Number(v), 2, 2) : "-"),
              })),
            }
          : {
              title: b.cusname,
              key: b.cuscode,
              dataIndex: `sum|${b.cuscode}`,
              align: "right",
              // ขยายความกว้างตามความยาวชื่อลูกค้า (ขั้นต่ำ 130 สูงสุด 260)
              width: Math.min(260, Math.max(130, b.cusname.length * 11 + 40)),
              render: (v) => (v ? comma(Number(v), 2, 2) : "-"),
              sorter: (a, b2) =>
                (a[`sum|${b.cuscode}`] || 0) - (b2[`sum|${b.cuscode}`] || 0),
            }
      ),
      {
        title: "ผลรวมทั้งสิ้น",
        key: "total",
        dataIndex: "total",
        fixed: "right",
        align: "right",
        width: 120,
        render: (v) => (
          <Typography.Text strong>
            {comma(Number(v || 0), 2, 2)}
          </Typography.Text>
        ),
        sorter: (a, b) => (a.total || 0) - (b.total || 0),
      },
    ];
    return cols;
  }, [branches, displayMode]);

  // รายการ key ของคอลัมน์ข้อมูลตามโหมดที่แสดง (ใช้กับแถวรวมและ Excel)
  const dataColKeys = useMemo(() => {
    if (displayMode === "daily") {
      const keys = [];
      branches.forEach((b) =>
        b.dates.forEach((d) =>
          keys.push({
            key: `${b.cuscode}|${d}`,
            branch: b.cusname,
            label: dayjs(d).format("DD/MM/YYYY"),
          })
        )
      );
      return keys;
    }
    return branches.map((b) => ({
      key: `sum|${b.cuscode}`,
      branch: b.cusname,
      label: b.cusname,
    }));
  }, [branches, displayMode]);

  /* ===== Export Excel (.xlsx) ===== */
  const handleExportExcel = () => {
    if (rows.length === 0) {
      message.warning("ไม่มีข้อมูลสำหรับส่งออก");
      return;
    }
    const flatCols = dataColKeys;
    const isDaily = displayMode === "daily";

    // แถวหัวตาราง: โหมดรายวันมี 2 ชั้น (สาขา merge / วันที่), โหมดสรุปมีชั้นเดียว
    const header1 = ["รหัสสินค้า", "ชื่อสินค้า"];
    const header2 = ["", ""];
    if (isDaily) {
      branches.forEach((b) => {
        b.dates.forEach((d, i) => {
          header1.push(i === 0 ? b.cusname : "");
          header2.push(dayjs(d).format("DD/MM/YYYY"));
        });
      });
    } else {
      branches.forEach((b) => header1.push(b.cusname));
    }
    header1.push("ผลรวมทั้งสิ้น");
    if (isDaily) header2.push("");

    const body = rows.map((r) => [
      r.stcode,
      r.stname,
      ...flatCols.map((c) => Number(r[c.key] || 0)),
      Number(r.total || 0),
    ]);
    const footer = [
      "",
      "รวม",
      ...flatCols.map((c) =>
        rows.reduce((s, r) => s + Number(r[c.key] || 0), 0)
      ),
      rows.reduce((s, r) => s + Number(r.total || 0), 0),
    ];

    const sheetRows = isDaily
      ? [header1, header2, ...body, footer]
      : [header1, ...body, footer];
    const ws = XLSX.utils.aoa_to_sheet(sheetRows);

    // merge หัวตาราง (เฉพาะโหมดรายวัน)
    if (isDaily) {
      const merges = [
        { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } }, // รหัสสินค้า
        { s: { r: 0, c: 1 }, e: { r: 1, c: 1 } }, // ชื่อสินค้า
      ];
      let col = 2;
      branches.forEach((b) => {
        if (b.dates.length > 1) {
          merges.push({ s: { r: 0, c: col }, e: { r: 0, c: col + b.dates.length - 1 } });
        }
        col += b.dates.length;
      });
      merges.push({ s: { r: 0, c: col }, e: { r: 1, c: col } }); // ผลรวมทั้งสิ้น
      ws["!merges"] = merges;
    }

    // ความกว้างคอลัมน์
    ws["!cols"] = [
      { wch: 12 },
      { wch: 30 },
      ...flatCols.map((c) => ({ wch: Math.max(12, (c.label || "").length + 2) })),
      { wch: 14 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "OrderItemsByBranch");
    XLSX.writeFile(
      wb,
      `order-items-by-branch-${dayjs().format("YYYYMMDD-HHmm")}.xlsx`
    );
  };

  const summaryRow = () => {
    if (rows.length === 0) return null;
    const cells = [
      <Table.Summary.Cell index={0} key="t">
        <Typography.Text strong>รวม</Typography.Text>
      </Table.Summary.Cell>,
    ];
    let idx = 1;
    dataColKeys.forEach((c) => {
      const sum = rows.reduce((s, r) => s + Number(r[c.key] || 0), 0);
      cells.push(
        <Table.Summary.Cell index={idx} key={c.key} align="right">
          <Typography.Text strong>
            {sum ? comma(sum, 2, 2) : "-"}
          </Typography.Text>
        </Table.Summary.Cell>
      );
      idx++;
    });
    cells.push(
      <Table.Summary.Cell index={idx} key="grand" align="right">
        <Typography.Text strong>
          {comma(rows.reduce((s, r) => s + Number(r.total || 0), 0), 2, 2)}
        </Typography.Text>
      </Table.Summary.Cell>
    );
    return (
      <Table.Summary fixed>
        <Table.Summary.Row>{cells}</Table.Summary.Row>
      </Table.Summary>
    );
  };

  return (
    <div className="report-order-items">
      <Space
        direction="vertical"
        size="middle"
        style={{ display: "flex", position: "relative" }}
      >
        <Card>
          <Flex align="center" justify="space-between" wrap="wrap" gap={8}>
            <div>
              <Typography.Title level={3} className="m-0 !text-zinc-800">
                รายการสั่งซื้อตามสาขา (Order Items By Branch)
              </Typography.Title>
              <Typography.Text type="secondary">
                การวิเคราะห์เชิงลึกของการสั่งซื้อทั้งหมดของคุณ
              </Typography.Text>
            </div>
            <Button
              type="primary"
              className="bn-action bn-excel"
              icon={<FileExcelOutlined />}
              onClick={handleExportExcel}
            >
              ส่งออกเป็น Excel
            </Button>
          </Flex>
          <br />
          <Form form={form} layout="vertical" autoComplete="off">
            <Row gutter={[8, 8]}>
              <Col xs={24} sm={12} md={7} lg={7}>
                <Form.Item label="สาขา/ลูกค้า (ไม่จำเป็น)" name="cuscode">
                  <Select
                    size="large"
                    mode="multiple"
                    allowClear
                    showSearch
                    maxTagCount="responsive"
                    placeholder="ทุกสาขา"
                    optionFilterProp="label"
                    options={customerOptions}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6} lg={6}>
                <Form.Item label="สถานะสั่งซื้อ (ไม่จำเป็น)" name="doc_status">
                  <Select
                    size="large"
                    mode="multiple"
                    allowClear
                    maxTagCount="responsive"
                    placeholder="ทุกสถานะ (ยกเว้นยกเลิก)"
                    options={statusOptions}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={16} md={7} lg={7}>
                <Form.Item label="วันที่สั่งซื้อ" name="daterange">
                  <RangePicker
                    style={{ width: "100%", height: 40 }}
                    format="DD/MM/YYYY"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8} md={4} lg={4}>
                <Form.Item label=" ">
                  <Button
                    type="primary"
                    className="bn-action width-100"
                    icon={<CheckOutlined />}
                    onClick={getReport}
                  >
                    นำมาใช้
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
          <Flex justify="space-between" align="center" style={{ marginBottom: 8 }}>
            <Segmented
              value={displayMode}
              onChange={setDisplayMode}
              options={[
                { value: "branch", label: "สรุปตามสาขา" },
                { value: "daily", label: "แยกรายวัน" },
              ]}
            />
            <Typography.Text type="secondary">
              {rows.length > 0 && `${rows.length} รายการ / ${branches.length} สาขา`}
            </Typography.Text>
          </Flex>
          <Table
            size="small"
            rowKey="stcode"
            columns={columns}
            dataSource={rows}
            bordered
            pagination={false}
            sticky
            scroll={{ x: "max-content", y: "calc(100vh - 420px)" }}
            summary={summaryRow}
          />
        </Card>
      </Space>
    </div>
  );
};

export default OrderItemsByBranch;
